import { FAIcon } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { useState } from 'react'
import { useModuleFolderLabels } from '../hooks'
import type { LEARNING_FOLDERS_QUERY } from '../queries'
import { LearningDemoCard } from './learning-demo-card'

type LearningFolder = ResultOf<
  typeof LEARNING_FOLDERS_QUERY
>['learningFolders'][number]

interface Props {
  folder: LearningFolder
  defaultOpen?: boolean
}

export function LearningFolder({ folder, defaultOpen }: Props) {
  const translations = useTranslate({ completed: 'learning.completed' })

  const moduleLabel = useModuleFolderLabels()

  const allCompleted = folder.total > 0 && folder.completed === folder.total

  // A folder the user has finished starts collapsed, so the page opens on the
  // demos they still have left to watch.
  const [open, setOpen] = useState(defaultOpen ?? !allCompleted)

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <header
        className="flex select-none items-center justify-between px-5 py-4 sm:px-6"
        role="button"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <div className="flex items-center gap-2">
          <FAIcon
            className="text-stone-500"
            icon={open ? 'chevron-down' : 'chevron-right'}
            fixedWidth
          />

          <h3 className="text-lg font-semibold text-stone-900">
            {moduleLabel(folder.module)}
          </h3>
        </div>

        <span
          className={
            allCompleted
              ? 'inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900'
              : 'inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700'
          }
        >
          {folder.completed}/{folder.total} {translations.completed}
        </span>
      </header>

      {open && (
        <ul className="border-t border-stone-200">
          {folder.demos.map(demo => (
            <LearningDemoCard key={demo.id} demo={demo} />
          ))}
        </ul>
      )}
    </section>
  )
}
